import { Label, Switch as TamaguiSwitch, View } from 'tamagui'
import {
  type SegmentAction,
  type SegmentComponent,
  triggerSegmentAction,
} from '@/src/services/segment'
import { useEffect, useId, useState, type ComponentProps, type JSX } from 'react'
import './fix-switch-thumb-vertical-positioning-on-web.css'
import { mesaColorNames } from '@/src/common/theme'
import { vibrateError, vibrateSuccess } from '@/src/common/utils/vibrate'
import { Platform, StyleSheet } from 'react-native'

interface ISwitchComponentProps extends ComponentProps<typeof TamaguiSwitch> {
  segmentAction?: SegmentAction
  segmentComponent?: SegmentComponent
  thumbProps?: ComponentProps<typeof TamaguiSwitch.Thumb>
  /** use this to render an accompanying label which, when clicked, will toggle the switch */
  label?: {
    props?: ComponentProps<typeof Label>
    viewProps?: ComponentProps<typeof View>
    /** parameterizes the {@link props}.switchComponent, allowing you to render whatever custom label you want */
    render: (props: { switchComponent: JSX.Element }) => JSX.Element
  }
}

export function Switch({
  segmentAction,
  segmentComponent = 'Switch',
  thumbProps,
  label,
  ...restOfProps
}: ISwitchComponentProps) {
  const [checked, setChecked] = useState(restOfProps.checked)
  const internalId = useId()
  const id = restOfProps.id || internalId

  useEffect(() => setChecked(restOfProps.checked), [restOfProps.checked])

  const switchComponent = (
    <TamaguiSwitch
      className="fix-switch-thumb-vertical-positioning-on-web"
      size="$2"
      animation="quick"
      backgroundColor={
        checked
          ? mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9']
          : mesaColorNames['global/grey/20 #E6E9EB']
      }
      {...restOfProps}
      id={id}
      onCheckedChange={(...args) => {
        restOfProps.onCheckedChange?.(...args)
        setChecked(args[0])

        void (args[0] ? vibrateSuccess() : vibrateError())

        if (segmentAction) {
          void triggerSegmentAction({
            component: segmentComponent,
            action: segmentAction,
          })
        }
      }}
      style={[{ flexDirection: 'row', cursor: 'pointer' }, restOfProps.style]}
    >
      <TamaguiSwitch.Thumb
        animation="bouncy"
        backgroundColor={
          checked
            ? mesaColorNames['Greyscale/500 #6E7375']
            : mesaColorNames['Greyscale/400 #A4AAAC']
        }
        height={18}
        width={18}
        {...(Platform.OS === 'web' ? { transform: [{ translateX: checked ? 6 : -6 }] } : undefined)}
        {...thumbProps}
      />
    </TamaguiSwitch>
  )

  return label ? (
    <Label htmlFor={id} asChild {...label.props}>
      <View
        animation="quick"
        pressStyle={{ opacity: 0.5 }}
        htmlFor={id}
        tag="label"
        {...label.viewProps}
        style={StyleSheet.flatten([
          { flexDirection: 'row', alignItems: 'center', cursor: 'pointer', gap: 10 },
          StyleSheet.flatten([label.viewProps?.style]),
        ] as const)}
      >
        {label.render({ switchComponent })}
      </View>
    </Label>
  ) : (
    switchComponent
  )
}
